import { Class } from '@suntex.core/types';

import { defaultLogMethod } from './default-log-method.fn';
import { StandardLoggerBase } from './standard-logger.base';
import {
  BasicLogger,
  ClassLogger,
  ContextFrame,
  ContextualLogger,
  LogEntry,
  LogLevel,
  LogMethod,
  LoggerOpts,
  StandardLogger,
} from './types';

/*
 * Note: This class is using a singleton pattern rather DI so that it can be used from a static context
 */
export class Logger<T extends object = object>
  extends StandardLoggerBase
  implements ContextualLogger, ClassLogger<T>
{
  private static readonly globalLogger = new Logger(Logger);

  private static logMethod: LogMethod = defaultLogMethod;

  private readonly children: Record<string, Logger> = {};

  private get contextString(): string {
    if (this._contextString !== undefined) {
      return this._contextString;
    } else {
      const contextStringStack: string[] = stringifyContextStack(
        this.contextStack,
      );
      this.contextStack = contextStringStack;
      this._contextString = buildContextString(contextStringStack);
      return this._contextString;
    }
  }

  private _contextString?: string;

  constructor(
    private basicLogger: BasicLogger,
    private contextStack: ContextFrame[] = [],
    private logLevel: LogLevel = LogLevel.Info,
  ) {
    super();
  }

  log(e: LogEntry): void {
    if (e.level <= this.logLevel) {
      this.basicLogger.log({
        ...e,
        context: e.context
          ? buildContextString(
              stringifyContextStack([...this.contextStack, e.context]),
            )
          : this.contextString,
      });
    }
  }

  for(context: readonly string[], opts?: LoggerOpts): ContextualLogger;
  for<T extends object>(
    context: () => Class<T>,
    opts?: LoggerOpts,
  ): ClassLogger<T>;
  for<T extends object>(
    context: readonly string[] | (() => Class<T>),
    opts?: LoggerOpts,
  ): ClassLogger<T> | ContextualLogger;
  for<T extends object>(
    context: readonly string[] | (() => Class<T>),
    opts?: LoggerOpts,
  ): ClassLogger<T> | ContextualLogger {
    if (typeof context === 'function') {
      return new Logger(
        this.basicLogger,
        [...this.contextStack, context],
        opts?.level,
      );
    } else {
      return this.descendent(context, opts?.level);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  forMethod<T>(method: (this: T, ...args: any[]) => any): StandardLogger {
    return this.descendent([`.${method.name}(...)`]);
  }

  private descendent(
    contexts: readonly string[],
    logLevel?: LogLevel,
  ): ContextualLogger {
    if (contexts.length) {
      const [context, ...rest] = contexts;
      const logger = context
        ? this.children[context] ||
          (this.children[context] = new Logger(
            this.basicLogger,
            [...this.contextStack, context],
            logLevel ?? this.logLevel,
          ))
        : this;
      return logger.descendent(rest, logLevel);
    } else {
      this.logLevel = logLevel ?? this.logLevel;
      return this;
    }
  }

  static configure(opts: Partial<{ logMethod: LogMethod }>): void {
    if (opts.logMethod) {
      Logger.logMethod = opts.logMethod;
    }
  }

  static for(
    context: string | readonly string[],
    opts?: LoggerOpts,
  ): ContextualLogger;
  static for<T extends object>(
    context: () => Class<T>,
    opts?: LoggerOpts,
  ): ClassLogger<T>;
  static for<T extends object>(
    context: string | readonly string[] | (() => Class<T>),
    opts?: LoggerOpts,
  ): ContextualLogger | ClassLogger<T> {
    return Logger.globalLogger.for(
      typeof context === 'string' ? [context] : context,
      opts,
    );
  }

  static global(): ContextualLogger {
    return Logger.globalLogger;
  }

  static log(e: LogEntry): void {
    Logger.logMethod(e);
  }
}

function buildContextString(contextStack: string[]): string {
  return contextStack.join(' : ');
}

function stringifyContextStack(contextStack: ContextFrame[]): string[] {
  return contextStack.map((context) =>
    typeof context === 'string'
      ? context
      : context.name
        ? context.name
        : (context as () => Class<object>)().name,
  );
}
